import NumberInput from '../../../components/NumberInput';
import { useEffect, useRef, useState } from 'react';

import FrameSection from '../../../components/dnd/FrameSection';
import TableInventory from '../../../components/dnd/inventory/TableInventory';
import { WeightUnit } from '../../../components/dnd/inventory/Weight';
import { useAuth } from '../../../hooks/useAuth';

type CatalogItem = {
    id: number;
    source: number;
    name: string;
    description: string;
    weight: number;
    value_amount: number;
    value_currency: string;
};

type Value = { cp: number; sp: number; ep: number; gp: number; pp: number };

type ItemReference = {
    id: number;
    item_id: number | null;
    name: string;
    quantity: number;
    weight: number;
    notes: string | null;
    value?: Value;
};

type PendingItem = Omit<ItemReference, 'id'> & { id: string };
type MiscellaneousItem = ItemReference | PendingItem;

type MiscellaneousProps = {
    characterId: number;
    items: ItemReference[];
    onWeightChange?: (weight: number) => void;
    weightUnit: WeightUnit;
};

function Miscellaneous({ characterId, items, onWeightChange, weightUnit }: MiscellaneousProps) {
    const { apiRequest } = useAuth();
    const asValue = (item: CatalogItem): Value => ({ cp: item.value_currency.toLowerCase() === 'cp' ? item.value_amount : 0, sp: item.value_currency.toLowerCase() === 'sp' ? item.value_amount : 0, ep: item.value_currency.toLowerCase() === 'ep' ? item.value_amount : 0, gp: item.value_currency.toLowerCase() === 'gp' ? item.value_amount : 0, pp: item.value_currency.toLowerCase() === 'pp' ? item.value_amount : 0 });
    const [inventory, setInventory] = useState<MiscellaneousItem[]>(items.map((item) => ({ ...item, value: item.value || { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 } })));
    const [catalog, setCatalog] = useState<CatalogItem[]>([]);
    const [error, setError] = useState('');
    const nextPendingId = useRef(1);

    useEffect(() => setInventory(items.map((item) => ({ ...item, value: item.value || { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 } }))), [items]);
    useEffect(() => {
        onWeightChange?.(inventory.reduce((total, item) => total + item.weight * item.quantity, 0));
    }, [inventory, onWeightChange]);

    useEffect(() => {
        apiRequest<{ success: boolean; data: CatalogItem[] }>('/api/items/', 'GET')
            .then((result) => setCatalog(result.data))
            .catch(() => setError('Could not load the item catalog.'));
    }, [apiRequest]);

    const addCatalogItem = async (catalogItem: CatalogItem) => {
        try {
            const result = await apiRequest<{ success: boolean; data: ItemReference }>(`/api/characters/${characterId}/items`, 'POST', {
                item_id: catalogItem.id,
                quantity: 1,
            });
            setInventory((current) => {
                const existing = current.find((item) => item.item_id === catalogItem.id);
                if (existing) {
                    return current.map((item) => item.item_id === catalogItem.id
                        ? { ...item, quantity: result.data.quantity }
                        : item);
                }
                return [...current, { ...catalogItem, ...result.data, value: asValue(catalogItem) }];
            });
            setError('');
        } catch {
            setError('Could not add the item.');
        }
    };

    const saveItem = async (item: MiscellaneousItem) => {
        try {
            if (typeof item.id === 'string') {
                const result = await apiRequest<{ success: boolean; data: ItemReference }>(`/api/characters/${characterId}/items`, 'POST', item);
                setInventory((current) => current.map((currentItem) => currentItem.id === item.id ? result.data : currentItem));
            } else {
                await apiRequest(`/api/characters/${characterId}/items/${item.id}`, 'PUT', item);
            }
            setError('');
            return true;
        } catch {
            setError('Could not save item changes.');
            return false;
        }
    };

    const deleteItem = async (item: MiscellaneousItem) => {
        if (typeof item.id === 'string') return true;
        try {
            await apiRequest(`/api/characters/${characterId}/items/${item.id}`, 'DELETE');
            setError('');
            return true;
        } catch {
            setError('Could not delete the item.');
            return false;
        }
    };

    return (
        <>
            <FrameSection title="Miscellaneous" />
            {error && <div className="alert alert-danger" role="alert">{error}</div>}
            <TableInventory
                className="inventory-table--miscellaneous"
                thead={<>
                    <th scope="col">Name</th>
                    <th scope="col" className="text-center" style={{ width: 80 }}><i className="fa fa-hashtag" aria-label="Quantity" /></th>
                </>}
                searchableItems={catalog.filter((catalogItem) => !inventory.some((item) => item.item_id === catalogItem.id))}
                items={inventory}
                setItems={setInventory}
                sorted
                sortedSearchable
                onSelect={addCatalogItem}
                onSave={saveItem}
                onDelete={deleteItem}
                getDescription={(item) => item.item_id === null ? null : catalog.find((current) => current.id === item.item_id)?.description}
                onNotesChange={(item, notes) => setInventory(inventory.map((current) => current.id === item.id ? { ...current, notes } : current))}
                getWeight={(item) => item.weight}
                weightUnit={weightUnit}
                onWeightChange={(item, weight) => setInventory(inventory.map((current) => current.id === item.id ? { ...current, weight } : current))}
                canEditWeight={(item) => item.item_id === null}
                getValue={(item) => item.value || { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 }}
                onValueChange={(item, value) => setInventory(inventory.map((current) => current.id === item.id ? { ...current, value } : current))}
                canEditValue={(item) => item.item_id === null}
                canSave={(item) => item.name.trim().length > 0}
                isUnsavedItem={(item) => typeof item.id === 'string'}
                columnCount={2}
                emptyMessage="You don't have any miscellaneous items."
                getKey={(item) => String(item.id)}
                getLabel={(item) => item.name}
                newItem={() => ({ id: `new-${nextPendingId.current++}`, item_id: null, name: '', quantity: 1, weight: 0, notes: null, value: { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 } })}
                renderItem={(item, isEditing) => {
                    const isCustom = item.item_id === null;
                    return <>
                        <td>{isEditing && isCustom ? <input type="text" className="form-control" value={item.name} onChange={(event) => setInventory(inventory.map((current) => current.id === item.id ? { ...current, name: event.target.value } : current))} aria-label="Custom item name" /> : item.name}</td>
                        <td className="text-center">{isEditing ? <NumberInput min={1} className="form-control" value={item.quantity} onValueChange={(quantity) => setInventory(inventory.map((current) => current.id === item.id ? { ...current, quantity } : current))} aria-label={`Quantity of ${item.name || 'custom item'}`} /> : item.quantity}</td>
                    </>;
                }}
            />
        </>
    );
}

export default Miscellaneous;
